// The Horoscope screen's state (docs/01-Product/Horoscope.md): the night's
// constellations and its horoscope from the Worker, the player's three
// picks (kept in this browser per night until the horoscope is written), and
// the write. A write lasts at least the divining's minimum, so a fast answer
// still gets its moment, and once written it waits for Reveal Horoscope; a
// write already under way elsewhere is polled.
import { api, ApiError } from '../api/client';
import type { Horoscope, HoroscopeResponse, Sign } from '../api/types';
import { createStore } from '../store';

export const SIGNS: readonly Sign[] = ['sun', 'moon', 'rising'];

export type Picks = Partial<Record<Sign, string>>;

export interface HoroscopeState {
  phase: 'idle' | 'loading' | 'ready' | 'error';
  /** The past night asked for, or null for tonight. */
  requested: string | null;
  data: HoroscopeResponse | null;
  /** Constellation ids chosen per sign. */
  picks: Picks;
  /** A write in flight: the divining runs while this is set. */
  writing: boolean;
  /** Written while the player watched: "The stars have spoken." waits for Reveal Horoscope. */
  awaitingReveal: boolean;
  /** The horoscope arrived in this visit (its text is revealed line by line). */
  justWritten: boolean;
  /** Why the last write failed. */
  writeError: string | null;
  loadError: string | null;
}

const initial: HoroscopeState = {
  phase: 'idle',
  requested: null,
  data: null,
  picks: {},
  writing: false,
  awaitingReveal: false,
  justWritten: false,
  writeError: null,
  loadError: null,
};

export const horoscopeStore = createStore<HoroscopeState>(initial);

const POLL_MS = 2500;
let loadToken = 0;
let pollTimer: ReturnType<typeof setTimeout> | null = null;

const picksKey = (date: string) => `wits.horoscope.picks.${date}`;

function storedPicks(date: string): Picks {
  try {
    const raw = localStorage.getItem(picksKey(date));
    return raw ? (JSON.parse(raw) as Picks) : {};
  } catch {
    return {};
  }
}

function storePicks(date: string, picks: Picks): void {
  try {
    localStorage.setItem(picksKey(date), JSON.stringify(picks));
  } catch {
    // Private mode or blocked storage: the picks last as long as the page.
  }
}

/** Picks that still name one of the night's constellations. */
function validPicks(picks: Picks, data: HoroscopeResponse): Picks {
  const ids = new Set(data.constellations.map((c) => c.id));
  const out: Picks = {};
  for (const sign of SIGNS) {
    const id = picks[sign];
    if (id && ids.has(id)) out[sign] = id;
  }
  return out;
}

/** The picks a horoscope was written with, or the browser's for the night. */
function picksFor(data: HoroscopeResponse): Picks {
  const h = data.horoscope;
  if (h) return { sun: h.signs.sun.id, moon: h.signs.moon.id, rising: h.signs.rising.id };
  return validPicks(storedPicks(data.date), data);
}

function stopPolling(): void {
  if (pollTimer) clearTimeout(pollTimer);
  pollTimer = null;
}

/** Load tonight's (null) or a past night's Horoscope; the same night again refreshes in place. */
export async function loadHoroscope(date: string | null): Promise<void> {
  const token = ++loadToken;
  stopPolling();
  const current = horoscopeStore.get();
  const same = current.requested === date && current.data !== null;
  horoscopeStore.set(same ? { ...current, loadError: null } : { ...initial, phase: 'loading', requested: date });
  try {
    const data = await api.horoscope(date);
    if (token !== loadToken) return;
    const s = horoscopeStore.get();
    const writingElsewhere = data.horoscope?.status === 'writing';
    horoscopeStore.set({
      ...s,
      phase: 'ready',
      data,
      picks: s.writing ? s.picks : picksFor(data),
      writing: s.writing || writingElsewhere,
      loadError: null,
    });
    if (writingElsewhere) pollUntilWritten(token, data.date);
  } catch (err) {
    if (token !== loadToken) return;
    const message = err instanceof ApiError ? err.message : 'The sky is out of reach. Check your connection.';
    horoscopeStore.set({ ...horoscopeStore.get(), phase: 'error', loadError: message });
  }
}

/** A write started in another tab (or before a reload): look again until it is written or has failed. */
function pollUntilWritten(token: number, date: string): void {
  stopPolling();
  pollTimer = setTimeout(async () => {
    try {
      const data = await api.horoscope(date);
      if (token !== loadToken) return;
      const status = data.horoscope?.status;
      if (status === 'writing') return pollUntilWritten(token, date);
      horoscopeStore.set({
        ...horoscopeStore.get(),
        data,
        picks: picksFor(data),
        writing: false,
        awaitingReveal: status === 'written',
        justWritten: status === 'written',
        writeError: status === 'failed' ? CLOUDED : null,
      });
    } catch {
      if (token === loadToken) pollUntilWritten(token, date);
    }
  }, POLL_MS);
}

/** Choose a constellation for a sign, or clear it with null. */
export function pickSign(sign: Sign, id: string | null): void {
  const s = horoscopeStore.get();
  if (!s.data || s.data.horoscope?.status === 'written' || s.writing) return;
  const picks = { ...s.picks };
  if (id) picks[sign] = id;
  else delete picks[sign];
  storePicks(s.data.date, picks);
  horoscopeStore.set({ ...s, picks, writeError: null });
}

export function allPicked(picks: Picks): picks is Record<Sign, string> {
  return SIGNS.every((sign) => !!picks[sign]);
}

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const CLOUDED = 'The stars are clouded over. Try again in a moment.';

function writeErrorOf(err: unknown): string {
  // A refusal that says why (locked, a sign no longer among the night's constellations).
  if (err instanceof ApiError && err.status >= 400 && err.status < 500) return err.message;
  return CLOUDED;
}

/**
 * Write the night's horoscope from the picks; resolves no sooner than minMs
 * after it starts. A placeholder's horoscope may be written again (its picks
 * are its signs), which the real writer does once it is connected.
 */
export async function writeHoroscope(minMs: number): Promise<void> {
  const s = horoscopeStore.get();
  if (!s.data || s.writing || !allPicked(s.picks)) return;
  const token = loadToken;
  // Always the night the signs were chosen on: a write sent just after
  // midnight still belongs to it, within the Worker's grace.
  const date = s.data.date;
  horoscopeStore.set({ ...s, writing: true, awaitingReveal: false, justWritten: false, writeError: null });
  const started = Date.now();
  let horoscope: Horoscope | null = null;
  let writeError: HoroscopeState['writeError'] = null;
  try {
    horoscope = (await api.writeHoroscope(s.picks, date)).horoscope;
  } catch (err) {
    if (err instanceof ApiError && err.body?.error === 'horoscope_writing') {
      // Someone (another tab) is already writing it: wait for theirs.
      await wait(Math.max(0, minMs - (Date.now() - started)));
      if (token === loadToken) pollUntilWritten(token, date);
      return;
    }
    writeError = writeErrorOf(err);
  }
  await wait(Math.max(0, minMs - (Date.now() - started)));
  if (token !== loadToken) return;
  const now = horoscopeStore.get();
  horoscopeStore.set({
    ...now,
    data: horoscope && now.data ? { ...now.data, horoscope } : now.data,
    writing: false,
    awaitingReveal: horoscope !== null,
    justWritten: horoscope !== null,
    writeError,
  });
}

/** Reveal Horoscope: the divining closes on the horoscope written underneath. */
export function revealHoroscope(): void {
  horoscopeStore.set({ ...horoscopeStore.get(), awaitingReveal: false });
}

/** Leave the screen: stop any polling (the write itself carries on in the Worker). */
export function leaveHoroscope(): void {
  stopPolling();
}
